import { useState, useRef } from 'react';
import { Upload, X, Video, Loader2, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function detectVideoType(url) {
  if (!url) return null;
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo.com')) return 'vimeo';
  return 'upload';
}

function getEmbedUrl(url, type) {
  if (type === 'youtube') {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  }
  if (type === 'vimeo') {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? `https://player.vimeo.com/video/${match[1]}` : null;
  }
  return null;
}

export const VideoUpload = ({ value, videoType, onChange, placeholder = "URL de la vidéo (YouTube, Vimeo ou lien direct)" }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Fichier trop volumineux. Maximum: 100MB'); return; }

    setIsUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_BASE}/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded * 100) / p.total)),
      });
      onChange(response.data.url, 'upload');
      toast.success('Vidéo téléchargée avec succès !');
    } catch { toast.error("Erreur lors du téléchargement de la vidéo"); }
    finally { setIsUploading(false); setUploadProgress(0); }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    const type = detectVideoType(url);
    onChange(url, type);
  };

  const handleRemove = () => {
    onChange('', null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resolvedUrl = value && value.startsWith('/api/uploads/') ? `${BASE_URL}${value}` : value;
  const embedUrl = value ? getEmbedUrl(value, videoType) : null;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input type="text" placeholder={placeholder} value={value || ''} onChange={handleUrlChange} className="pr-10" disabled={isUploading} data-testid="video-url-input" />
          {value && <button type="button" onClick={handleRemove} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-red-500"><X size={16} /></button>}
        </div>
        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="shrink-0">
          {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
        </Button>
        <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" onChange={handleFileSelect} className="hidden" />
      </div>

      {isUploading && (
        <div className="w-full bg-neutral-200 rounded-full h-2">
          <div className="bg-emerald-500 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      {value && !isUploading && (
        <div className="relative rounded-lg overflow-hidden border border-neutral-200 bg-black">
          {embedUrl ? (
            <div className="aspect-video">
              <iframe src={embedUrl} className="w-full h-full" allowFullScreen title="Aperçu vidéo" />
            </div>
          ) : videoType === 'upload' || !videoType ? (
            <div className="aspect-video">
              <video src={resolvedUrl} controls className="w-full h-full" />
            </div>
          ) : (
            <div className="p-4 flex items-center gap-3 bg-neutral-50">
              <Video size={24} className="text-neutral-400" />
              <span className="text-sm text-neutral-600 truncate">{value}</span>
            </div>
          )}
          <button type="button" onClick={handleRemove} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10">
            <X size={14} />
          </button>
        </div>
      )}

      {!value && !isUploading && (
        <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors">
          <Video size={32} className="mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500">Cliquez pour télécharger ou glissez-déposez</p>
          <p className="text-xs text-neutral-400 mt-1">MP4, MOV, WEBM jusqu'à 100MB</p>
        </div>
      )}
    </div>
  );
};

export { detectVideoType, getEmbedUrl };
export default VideoUpload;
